<template>
  <div class="ai-intro-page" :class="{ 'mobile-layout': isMobile }">
    <!-- 响应式横幅 -->
    <div class="responsive-banner">
      <img :src="bannerImage" alt="智能AI助手" class="banner-image">
    </div>

    <!-- 主要内容 -->
    <main class="main-content">
      <!-- 简介部分 -->
      <section class="intro-section">
        <h1 class="section-title">
          <i class="icon icon-ai"></i>
          <span>智能AI助手</span>
        </h1>
        <p class="section-description">
          我们的智能AI是基于最先进的自然语言处理技术打造的人工智能助手，
          具备强大的理解、学习和创造能力，能够为用户提供全天候的智能服务。
        </p>
      </section>

      <!-- 功能特点 -->
      <feature-section :features="features" :is-mobile="isMobile" />

      <!-- 应用场景 -->
      <section class="scene-section">
        <h2 class="section-title">
          <i class="icon icon-scene"></i>
          <span>应用场景</span>
        </h2>
        <div class="scene-list">
          <div v-for="(scene, index) in scenes" :key="index" class="scene-item">
            <span class="scene-index">{{ (index + 1).toString().padStart(2, '0') }}</span>
            <p class="scene-text">{{ scene }}</p>
          </div>
        </div>
      </section>

      <!-- 技术优势 -->
      <section class="tech-section">
        <h2 class="section-title">
          <i class="icon icon-tech"></i>
          <span>技术优势</span>
        </h2>
        <div class="tech-tags">
          <span v-for="(tech, index) in techs" :key="index" class="tech-tag">{{ tech }}</span>
        </div>
      </section>
    </main>

    <!-- 底部CTA -->
    <footer class="footer-cta">
      <button class="primary-btn" @click="handleExperience">立即体验</button>
      <button class="secondary-btn" @click="handleContact">联系我们</button>
    </footer>
  </div>
</template>

<script>
import FeatureSection from '@/components/FeatureSection.vue'

export default {
  components: {
    FeatureSection
  },
  data() {
    return {
      isMobile: false,
      windowWidth: 0,
      features: [
        {
          icon: 'icon-chat',
          title: '智能对话',
          desc: '自然流畅的对话体验，理解上下文语境'
        },
        {
          icon: 'icon-write',
          title: '内容创作',
          desc: '文章、诗歌、代码等各种内容创作'
        },
        {
          icon: 'icon-translate',
          title: '多语言翻译',
          desc: '支持100+种语言的精准翻译'
        },
        {
          icon: 'icon-learn',
          title: '持续学习',
          desc: '不断更新知识库，与时俱进'
        },
        {
          icon: 'icon-custom',
          title: '个性化定制',
          desc: '可根据需求定制专属AI助手'
        },
        {
          icon: 'icon-safe',
          title: '安全可靠',
          desc: '数据加密，保护用户隐私'
        }
      ],
      scenes: [
        '企业客服与智能问答系统',
        '教育培训与知识解答',
        '内容创作与文案生成',
        '数据分析与报告撰写',
        '编程辅助与代码生成',
        '多语言翻译与跨文化交流'
      ],
      techs: [
        '深度学习',
        '自然语言处理',
        '知识图谱',
        '多轮对话',
        '情感分析',
        '迁移学习',
        '联邦学习',
        '大语言模型'
      ]
    }
  },
  computed: {
    bannerImage() {
      return this.isMobile 
        ? require('@/assets/banner.jpg')
        : require('@/assets/banner.jpg')
    }
  },
  methods: {
    handleResize() {
      this.windowWidth = window.innerWidth
      this.isMobile = this.windowWidth <= 768
    },
    handleExperience() {
      // 跳转体验页面逻辑
      console.log('立即体验')
    },
    handleContact() {
      // 联系逻辑
      console.log('联系我们')
    }
  },
  mounted() {
    this.handleResize()
    window.addEventListener('resize', this.handleResize)
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.handleResize)
  }
}
</script>

<style scoped>
/* 基础变量 */
:root {
  --primary-color: #1890ff;
  --text-color: #333;
  --secondary-text: #666;
  --light-bg: #f8f9fa;
  --white: #fff;
  --border-color: #eee;
  --box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
}

/* 基础样式 */
.ai-intro-page {
  font-family: 'PingFang SC', 'Microsoft YaHei', sans-serif;
  color: var(--text-color);
  max-width: 1200px;
  margin: 0 auto;
  padding-bottom: 80px;
  background-color: var(--light-bg);
}

.ai-intro-page.mobile-layout {
  max-width: 100%;
  padding-bottom: 60px;
}

/* 响应式横幅 */
.responsive-banner {
  width: 100%;
  overflow: hidden;
}

.responsive-banner .banner-image {
  width: 100%;
  height: auto;
  display: block;
}

/* 主要内容区域 */
.main-content {
  padding: 0 40px;
}

.mobile-layout .main-content {
  padding: 0 20px;
}

/* 通用区块样式 */
.section-title {
  display: flex;
  align-items: center;
  font-size: 28px;
  margin: 40px 0 20px;
  color: var(--text-color);
}

.section-title .icon {
  margin-right: 12px;
  color: var(--primary-color);
  font-size: 32px;
}

.mobile-layout .section-title {
  font-size: 24px;
  margin: 30px 0 15px;
}

.mobile-layout .section-title .icon {
  font-size: 28px;
}

.section-description {
  font-size: 16px;
  line-height: 1.8;
  color: var(--secondary-text);
}

.mobile-layout .section-description {
  font-size: 14px;
}

/* 应用场景 */
.scene-list {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.scene-item {
  display: flex;
  align-items: center;
  padding: 18px 0;
  border-bottom: 1px solid var(--border-color);
}

.mobile-layout .scene-item {
  padding: 15px 0;
}

.scene-index {
  font-size: 28px;
  font-weight: bold;
  color: var(--primary-color);
  margin-right: 15px;
  min-width: 50px;
}

.mobile-layout .scene-index {
  font-size: 24px;
  min-width: 45px;
}

.scene-text {
  font-size: 16px;
  color: var(--text-color);
  flex: 1;
}

.mobile-layout .scene-text {
  font-size: 14px;
}

/* 技术标签样式 */
.tech-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
}

.tech-tag {
  background-color: rgba(24, 144, 255, 0.1);
  color: var(--primary-color);
  padding: 6px 14px;
  border-radius: 20px;
  font-size: 14px;
}

.mobile-layout .tech-tag {
  padding: 4px 12px;
  font-size: 12px;
}

/* 底部CTA按钮 */
.footer-cta {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  background: var(--white);
  padding: 16px;
  display: flex;
  justify-content: center;
  gap: 20px;
  box-shadow: 0 -2px 8px rgba(0, 0, 0, 0.1);
  z-index: 100;
}

.footer-cta button {
  padding: 12px 24px;
  border-radius: 6px;
  font-size: 16px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s;
}

.mobile-layout .footer-cta button {
  padding: 10px 20px;
  font-size: 14px;
}

.primary-btn {
  background: var(--primary-color);
  color: var(--white);
  border: none;
}

.primary-btn:hover {
  background: #40a9ff;
}

.secondary-btn {
  background: var(--white);
  color: var(--primary-color);
  border: 1px solid var(--primary-color);
}

.secondary-btn:hover {
  background: rgba(24, 144, 255, 0.05);
}

/* 移动端特定样式 */
@media (max-width: 768px) {
  .ai-intro-page {
    padding-bottom: 60px;
  }
  
  .section-title {
    font-size: 22px;
  }
  
  .footer-cta {
    padding: 12px;
    gap: 12px;
  }
}
</style>